if (!customElements.get('responsive-banner-slider')) {
  customElements.define('responsive-banner-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.sliderReady) return;
      this.track = this.querySelector('.responsive-banner__track');
      this.slides = Array.from(this.querySelectorAll('[data-banner-slide]'));
      if (!this.track || !this.slides.length) return;
      this.sliderReady = true;
      this.events = new AbortController();
      const on = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: this.events.signal });
      this.dots = Array.from(this.querySelectorAll('[data-banner-dot]'));
      this.pauseButton = this.querySelector('[data-banner-pause]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = Math.max(3000, Number(this.dataset.delay) || 3000);
      this.index = this.position = 0;
      this.visible = true;
      this.hovered = this.focused = this.paused = this.editorSelected = this.animating = false;
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.clones = [];
      if (this.slides.length > 1) {
        // Sentinels preserve the direction of travel at either end of the track.
        const clone = slide => {
          const copy = slide.cloneNode(true);
          copy.removeAttribute('data-banner-slide');
          copy.removeAttribute('data-shopify-editor-block');
          copy.dataset.bannerClone = '';
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          [copy, ...copy.querySelectorAll('[id]')].forEach(node => node.removeAttribute('id'));
          copy.querySelectorAll('a, button, [tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
          return copy;
        };
        this.clones = [clone(this.slides[this.slides.length - 1]), clone(this.slides[0])];
        this.track.prepend(this.clones[0]);
        this.track.append(this.clones[1]);
        this.position = 1;
      }
      const navigate = direction => { this.goTo(this.index + direction); this.schedule(); };
      on(this.querySelector('[data-banner-previous]'), 'click', () => navigate(-1));
      on(this.querySelector('[data-banner-next]'), 'click', () => navigate(1));
      this.dots.forEach(dot => on(dot, 'click', () => { this.goTo(Number(dot.dataset.bannerDot)); this.schedule(); }));
      on(this.pauseButton, 'click', () => {
        this.paused = !this.paused;
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.setAttribute('aria-label', this.paused ? 'Play banners' : 'Pause banners');
        this.pauseButton.querySelector('[data-banner-pause-icon]').textContent = this.paused ? '▶' : 'Ⅱ';
        this.schedule();
      });
      on(this, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        navigate(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(this, 'pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.stop();
      }, { passive: true });
      // Do not capture ordinary taps: they must reach the slide's anchor.
      on(window, 'pointerup', event => {
        if (!this.pointer || event.pointerId !== this.pointer.id) return;
        const dx = event.clientX - this.pointer.x, dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy)) {
          this.suppressClickUntil = Date.now() + 400;
          this.goTo(this.index + (dx < 0 ? 1 : -1));
        }
        this.schedule();
      }, { passive: true });
      on(window, 'pointercancel', () => { this.pointer = null; this.schedule(); }, { passive: true });
      on(this, 'click', event => {
        if (Date.now() < this.suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true });
      on(this, 'dragstart', event => event.preventDefault());
      on(this, 'mouseenter', () => { this.hovered = this.dataset.pauseHover === 'true'; this.schedule(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      on(this, 'focusin', () => { this.focused = true; this.stop(); });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => { this.finishTransition(); this.schedule(); });
      on(this.track, 'transitionend', event => { if (event.target === this.track && event.propertyName === 'transform') this.finishTransition(); });
      on(this, 'shopify:block:select', event => {
        const slide = event.target.closest?.('[data-banner-slide]');
        if (!slide || !this.slides.includes(slide)) return;
        this.editorSelected = true;
        this.finishTransition();
        this.goTo(this.slides.indexOf(slide), false);
        this.stop();
      });
      on(this, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: 0.15 });
        this.observer.observe(this);
      }
      this.update(false);
      this.schedule();
    }
    goTo(target, animate = true) {
      const count = this.slides.length;
      if (this.animating || count < 2) return;
      this.index = ((target % count) + count) % count;
      this.position = animate && target === count ? count + 1 : animate && target === -1 ? 0 : this.index + 1;
      this.animating = animate && !this.motion.matches;
      this.update(animate);
      if (this.animating) {
        clearTimeout(this.transitionTimer);
        this.transitionTimer = setTimeout(() => this.finishTransition(), 1100);
      } else this.finishTransition();
    }
    finishTransition() {
      clearTimeout(this.transitionTimer);
      this.animating = false;
      const position = this.slides.length > 1 ? this.index + 1 : 0;
      if (position !== this.position) { this.position = position; this.update(false); }
    }
    update(animate) {
      this.track.style.transitionDuration = animate && !this.motion.matches ? '' : '0s';
      this.track.style.transform = `translate3d(${-100 * this.position}%, 0, 0)`;
      this.dataset.activeSlide = String(this.index + 1);
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        slide.querySelectorAll('a, button, [tabindex]').forEach(control => active ? control.removeAttribute('tabindex') : control.setAttribute('tabindex', '-1'));
        if (active || index === (this.index + 1) % this.slides.length) slide.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }
    schedule() {
      this.stop();
      if (!this.autoplay || this.slides.length < 2 || this.motion.matches || document.hidden || !this.visible || this.hovered || this.focused || this.paused || this.editorSelected || this.pointer) return;
      this.timer = setTimeout(() => { this.goTo(this.index + 1); this.schedule(); }, this.delay);
    }
    stop() { clearTimeout(this.timer); }
    disconnectedCallback() {
      this.stop();
      clearTimeout(this.transitionTimer);
      this.events?.abort();
      this.observer?.disconnect();
      this.clones?.forEach(clone => clone.remove());
      this.sliderReady = false;
    }
  });
}
